"use client";

import { useState } from "react";
import { trackEvent } from "@/lib/analytics/track";
import { PLATFORM_INFO, PROMPTAI_API } from "@/lib/promptai";

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers / non-secure contexts.
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    el.remove();
    return ok;
  }
}

/** The full prompt text with copy and "copy & open in ChatGPT/Gemini". */
export default function PromptTextBox({ id, content, platforms }: { id: number; content: string; platforms: string[] }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const long = content.length > 900;

  async function copy(platform?: string) {
    const ok = await copyText(content);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
    trackEvent("prompt_copy", { promptId: id, platform: platform ?? null });
    // Same signal the app sends: feeds the trending score.
    fetch(`${PROMPTAI_API}/prompts/copy_count.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
      keepalive: true,
    }).catch(() => {});
    if (platform && PLATFORM_INFO[platform]) window.open(PLATFORM_INFO[platform].url, "_blank", "noopener");
  }

  const tryIn = (platforms.length ? platforms : ["chatgpt", "gemini"]).filter((p) => PLATFORM_INFO[p]);

  return (
    <div>
      <div className="overflow-hidden rounded-[1.25rem] bg-[#0b0f19] text-slate-100 shadow-[0_24px_50px_-28px_rgba(15,23,42,0.8)] ring-1 ring-white/10">
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
            </span>
            <span className="ml-2 text-xs font-medium text-white/50">
              Prompt · {content.split(/\s+/).filter(Boolean).length.toLocaleString("en-IN")} words
            </span>
          </div>
          <button
            type="button"
            onClick={() => copy()}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              copied ? "bg-emerald-400 text-emerald-950" : "bg-white text-[#0b0f19] hover:bg-emerald-300"
            }`}
          >
            {copied ? (
              <>
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="9" y="9" width="11" height="11" rx="2" />
                  <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                </svg>
                Copy prompt
              </>
            )}
          </button>
        </div>
        <div className="relative">
          <pre
            className={`whitespace-pre-wrap break-words px-4 py-4 font-mono text-[13px] leading-relaxed text-slate-200 sm:px-5 ${
              long && !expanded ? "max-h-80 overflow-hidden" : ""
            }`}
          >
            {content}
          </pre>
          {long && !expanded ? (
            <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-[#0b0f19] via-[#0b0f19]/90 to-transparent pb-4 pt-16">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white ring-1 ring-white/20 backdrop-blur transition hover:bg-white/20"
              >
                Show full prompt
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {tryIn.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => copy(p)}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:ring-slate-400 dark:bg-white/10 dark:text-white dark:ring-white/15"
          >
            Copy &amp; open {PLATFORM_INFO[p].label}
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M7 17L17 7M9 7h8v8" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}
